import { supabase } from '../lib/supabase';

const geoBucketBase = 'https://storage.googleapis.com/doddledays-1536605218990-geo';
const manifestUrl = `${geoBucketBase}/latest/manifest.json`;
const styleBase = `${geoBucketBase}/latest/styles`;
const openFreeMapStyleBase = 'https://tiles.openfreemap.org/styles';
const geoAccessUrl = (import.meta.env.VITE_GEO_ACCESS_URL as string | undefined)?.replace(/\/$/, '');
const privateMapValidationRange = 'bytes=0-16383';
const activeAccountStatuses = new Set(['active', 'trialing', 'past_due']);

type GeoSession = {
  manifest: Record<string, unknown>;
  styles: {
    positron: Record<string, unknown>;
    dark: Record<string, unknown>;
  };
  expires_at: string;
};

type CachedGeoSession = {
  value: GeoSession;
  expiresAt: number;
};

type NativeFetch = typeof window.fetch;

type PmtilesWindow = Window & {
  pmtiles?: { Protocol?: unknown };
  __doddledaysPmtilesProtocolInstalled?: boolean;
};

let installed = false;
let cached: CachedGeoSession | null = null;
let inFlight: Promise<GeoSession> | null = null;
let privateMapValidatedUntil = 0;

function requestUrl(input: Parameters<NativeFetch>[0]): string {
  if (typeof input === 'string') return input;
  if (input instanceof URL) return input.toString();
  return input.url;
}

function jsonResponse(value: unknown): Response {
  return new Response(JSON.stringify(value), {
    status: 200,
    headers: {
      'Cache-Control': 'private, no-store',
      'Content-Type': 'application/json; charset=utf-8',
      'X-Doddledays-Geo': 'authenticated',
    },
  });
}

function styleName(url: string): 'positron' | 'dark' | null {
  if (url === `${styleBase}/positron.json`) return 'positron';
  if (url === `${styleBase}/dark.json`) return 'dark';
  return null;
}

function pmtilesProtocolAvailable(): boolean {
  if (typeof window === 'undefined') return false;
  const pmtilesWindow = window as PmtilesWindow;
  return Boolean(pmtilesWindow.pmtiles?.Protocol && pmtilesWindow.__doddledaysPmtilesProtocolInstalled);
}

function manifestBasemap(session: GeoSession): string | null {
  const value = session.manifest.basemap;
  return typeof value === 'string' && value.startsWith('https://storage.googleapis.com/') ? value : null;
}

async function validatePrivateMap(nativeFetch: NativeFetch, session: GeoSession): Promise<boolean> {
  if (!pmtilesProtocolAvailable()) return false;

  const now = Date.now();
  if (now < privateMapValidatedUntil) return true;

  const basemap = manifestBasemap(session);
  if (!basemap) return false;

  try {
    const response = await nativeFetch(basemap, {
      method: 'GET',
      headers: { Range: privateMapValidationRange },
      cache: 'no-store',
    });
    if (response.status !== 200 && response.status !== 206) return false;

    const expiresAt = Date.parse(session.expires_at);
    privateMapValidatedUntil = Math.min(
      Number.isFinite(expiresAt) ? expiresAt - 5 * 60_000 : now + 10 * 60_000,
      now + 30 * 60_000,
    );
    return true;
  } catch {
    return false;
  }
}

async function accessToken(): Promise<string | null> {
  const { data } = await supabase.auth.getSession();
  return data.session?.access_token ?? null;
}

async function hasGeoAccess(): Promise<boolean> {
  const { data, error } = await supabase
    .from('user_subscriptions')
    .select('status')
    .maybeSingle();
  return !error && Boolean(data?.status && activeAccountStatuses.has(data.status));
}

async function requestGeoSession(nativeFetch: NativeFetch, allowRefresh = true): Promise<GeoSession> {
  if (!geoAccessUrl) throw new Error('Geo access service is not configured');
  let token = await accessToken();
  if (!token) throw new Error('Authentication required');
  if (!await hasGeoAccess()) throw new Error('Account is inactive');

  let response = await nativeFetch(`${geoAccessUrl}/session`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    cache: 'no-store',
  });

  if (response.status === 401 && allowRefresh) {
    const { data } = await supabase.auth.refreshSession();
    token = data.session?.access_token ?? null;
    if (!token) throw new Error('Authentication required');
    response = await nativeFetch(`${geoAccessUrl}/session`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      cache: 'no-store',
    });
  }

  if (!response.ok) throw new Error(`Geo session unavailable (${response.status})`);
  return await response.json() as GeoSession;
}

async function geoSession(nativeFetch: NativeFetch): Promise<GeoSession> {
  const now = Date.now();
  if (cached && now < cached.expiresAt - 5 * 60_000) return cached.value;
  if (inFlight) return inFlight;

  inFlight = requestGeoSession(nativeFetch).then((value) => {
    const expiresAt = Date.parse(value.expires_at);
    cached = {
      value,
      expiresAt: Number.isFinite(expiresAt) ? expiresAt : Date.now() + 30 * 60_000,
    };
    return value;
  }).finally(() => {
    inFlight = null;
  });
  return inFlight;
}

async function publicStyleFallback(nativeFetch: NativeFetch, name: 'positron' | 'dark'): Promise<Response> {
  return nativeFetch(`${openFreeMapStyleBase}/${name}`, { cache: 'default' });
}

export function installAuthenticatedGeoFetch(): void {
  if (typeof window === 'undefined' || installed) return;
  installed = true;
  const nativeFetch = window.fetch.bind(window);

  window.fetch = (async (input: Parameters<NativeFetch>[0], init?: Parameters<NativeFetch>[1]) => {
    const url = requestUrl(input);
    if (url === manifestUrl) {
      try {
        return jsonResponse((await geoSession(nativeFetch)).manifest);
      } catch {
        return new Response('', { status: 404, headers: { 'X-Doddledays-Geo': 'authentication-required' } });
      }
    }

    const name = styleName(url);
    if (name) {
      try {
        const session = await geoSession(nativeFetch);
        if (await validatePrivateMap(nativeFetch, session)) {
          return jsonResponse(session.styles[name]);
        }
      } catch {
        // Fall through to the public style. A private-map outage should never make the map blank.
      }
      return publicStyleFallback(nativeFetch, name);
    }

    return nativeFetch(input, init);
  }) as NativeFetch;
}
